import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";

const SLIDES = [
  {
    img: "/mockups/v2_01_hero_aston_style.jpg",
    kicker: "AUTO NUEVO",
    title: "Paga el enganche\ncuando lo recibas",
    sub: "Proceso 100% digital. Aprobación en 24 horas máximo. Financiamiento a tu medida.",
    model: "Sentra 2026",
  },
  {
    img: "/mockups/v2_03_tesla_style_lineup.jpg",
    kicker: "NUESTRA FLOTA",
    title: "Un auto para\ncada camino",
    sub: "Sedán, camioneta o van de pasajeros. Elige el que se adapta a tu vida.",
    model: "Sentra · Frontier · Urvan",
  },
  {
    img: "/mockups/v2_02_rimac_transition_stripe.jpg",
    kicker: "FINANCIAMIENTO",
    title: "¿Problemas\ncon el crédito?",
    sub: "Trabajamos con los principales bancos y financieras del país para encontrar la opción que sí te aprueba.",
    model: "Aprobación en 24 horas",
  },
];

// Aston-Martin-style hero: single massive car, minimal navigation, editorial text bottom-left,
// side navigation arrows, model name bottom-right.
export default function HeroAston({ onScrollToCatalog }) {
  const [i, setI] = useState(0);
  const count = SLIDES.length;
  const next = useCallback(() => setI((v) => (v + 1) % count), [count]);
  const prev = useCallback(() => setI((v) => (v - 1 + count) % count), [count]);

  useEffect(() => {
    const t = setInterval(next, 8500);
    return () => clearInterval(t);
  }, [next]);

  const s = SLIDES[i];

  return (
    <section
      className="relative w-full h-[100vh] min-h-[640px] max-h-[960px] overflow-hidden bg-[#0B1623]"
      data-testid="hero-carousel"
      aria-label="Hero"
    >
      {/* Background image with slow Ken Burns zoom */}
      <AnimatePresence mode="sync">
        <motion.div
          key={i}
          initial={{ opacity: 0, scale: 1.06 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 1.02 }}
          transition={{ opacity: { duration: 1.2, ease: "easeInOut" }, scale: { duration: 8, ease: "linear" } }}
          className="absolute inset-0"
        >
          <img src={s.img} alt={s.kicker} className="w-full h-full object-cover" loading="eager" />
          {/* Very subtle bottom gradient for text legibility */}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0B1623]/60" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B1623]/30 via-transparent to-transparent" />
        </motion.div>
      </AnimatePresence>

      {/* Text overlay — positioned centered vertically so it never overlaps the horizontal light streak of slide 3 */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 h-full flex flex-col justify-center pt-20 pr-16 md:pr-12">
        {/* Motion.div ocupa altura NATURAL con min-h suficiente para el slide más grande
            ("Paga el enganche cuando lo recibas") — así el CTA queda debajo sin traslape. */}
        <AnimatePresence mode="wait">
          <motion.div
            key={`t-${i}`}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.9, ease: [0.25, 0.1, 0.25, 1] }}
            className="max-w-3xl min-h-[340px] md:min-h-[420px] lg:min-h-[480px] pointer-events-none"
          >
            <span
              className="font-rajdhani font-medium text-white/90 text-xs md:text-sm tracking-[0.4em] mb-5 md:mb-6 block"
              data-testid="hero-kicker"
            >
              {s.kicker}
            </span>
            <h1
              className="font-rajdhani font-semibold text-white leading-[0.95] tracking-tight text-shadow-lg text-5xl sm:text-6xl md:text-7xl lg:text-[88px] whitespace-pre-line"
              data-testid="hero-title"
            >
              {s.title}
            </h1>
            <p className="font-manrope text-white/85 text-base md:text-lg mt-6 md:mt-8 max-w-xl leading-relaxed">
              {s.sub}
            </p>
          </motion.div>
        </AnimatePresence>

        {/* CTAs fuera del AnimatePresence: pointer-events-auto asegura clickeabilidad. */}
        <div className="mt-8 md:mt-10 flex flex-wrap items-center gap-4 max-w-3xl relative z-20 pointer-events-auto">
          <a
            href="#catalogo"
            className="group inline-flex items-center gap-3 text-white font-rajdhani font-medium tracking-[0.2em] text-sm uppercase border-b border-white/30 pb-2 hover:border-[#F09A21] transition-colors duration-300 cursor-pointer"
            data-testid="hero-cta-buy"
          >
            Ver Catálogo
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
          </a>
          <a
            href="#como-funciona"
            className="group inline-flex items-center gap-3 text-white/70 font-rajdhani font-medium tracking-[0.2em] text-sm uppercase border-b border-transparent pb-2 hover:text-white hover:border-white/30 transition-colors duration-300"
            data-testid="hero-cta-how"
          >
            Cómo Funciona
          </a>
        </div>
      </div>

      {/* Side navigation arrows — Aston-Martin style */}
      <button
        onClick={prev}
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 flex items-center justify-center text-white/70 hover:text-white transition-colors"
        aria-label="Anterior"
        data-testid="hero-prev-btn"
      >
        <ChevronLeft className="w-8 h-8 stroke-[1.5]" />
      </button>
      <button
        onClick={next}
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 flex items-center justify-center text-white/70 hover:text-white transition-colors"
        aria-label="Siguiente"
        data-testid="hero-next-btn"
      >
        <ChevronRight className="w-8 h-8 stroke-[1.5]" />
      </button>

      {/* Bottom-right model label + dots — con margen derecho suficiente para no chocar con los FABs (chatbot + WhatsApp) */}
      <div className="absolute bottom-8 md:bottom-10 right-20 md:right-24 z-20 flex flex-col items-end gap-3">
        <AnimatePresence mode="wait">
          <motion.span
            key={`m-${i}`}
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            transition={{ duration: 0.5 }}
            className="font-rajdhani font-medium text-white/80 text-xs md:text-sm tracking-[0.3em] uppercase"
          >
            {s.model}
          </motion.span>
        </AnimatePresence>
        <div className="flex items-center gap-2">
          {SLIDES.map((_, k) => (
            <button
              key={k}
              onClick={() => setI(k)}
              className={`h-[2px] transition-all duration-500 ${k === i ? "w-12 bg-white" : "w-6 bg-white/30 hover:bg-white/60"}`}
              aria-label={`Slide ${k + 1}`}
              data-testid={`hero-dot-${k}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
